import { useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

const fmtDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const tone = (v) => (v == null ? "text-gray-400" : v > 365 ? "text-red-600" : v > 180 ? "text-amber-600" : "text-emerald-600");

const avgBy = (rows, k) => {
  const m = {};
  rows.forEach((r) => {
    if (r.dias_al_vender == null) return;
    const key = r[k] || "Sin modelo";
    m[key] = m[key] || { name: key, n: 0, sum: 0 };
    m[key].n += 1; m[key].sum += r.dias_al_vender;
  });
  return Object.values(m).map((x) => ({ ...x, avg: Math.round(x.sum / x.n) })).sort((a, b) => b.avg - a.avg);
};

const AvgList = ({ title, items, testid }) => (
  <div className="flex-1 min-w-[260px]" data-testid={testid}>
    <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 mb-2">{title}</div>
    {items.length ? items.map((x) => (
      <div key={x.name} className="flex items-center justify-between py-1.5 border-b border-gray-100 text-[14px]">
        <span className="font-semibold">{x.name} <span className="text-gray-400 text-[11px]">({x.n})</span></span>
        <span className={`font-black ${tone(x.avg)}`}>{x.avg} días</span>
      </div>
    )) : <div className="text-[12px] text-gray-400 font-semibold">Sin datos</div>}
  </div>
);

const COLS = [["chasis", "Chasis"], ["salon", "Showroom"], ["sucursal", "Sucursal"], ["modelo", "Modelo"],
  ["anio_modelo", "Año modelo"], ["vendedor", "Vendedor"], ["fecha_venta", "Fecha facturación"],
  ["dias_al_vender", "Días en inventario al facturar"]];

const rd = (v) => `RD$${Math.round(v || 0).toLocaleString("es-DO")}`;

function Billing({ items }) {
  const total = items.reduce((s, x) => s + x.total, 0);
  const billed = items.reduce((s, x) => s + x.billed, 0);
  const units = items.reduce((s, x) => s + x.units, 0);
  const withSales = items.filter((x) => x.total > 0);
  const top = [...withSales].sort((a, b) => b.total - a.total)[0];
  return (
    <div data-testid="zonas-hot-billing">
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">Total facturado por mes</div>
      <div className="text-[11px] font-semibold text-gray-400 mb-2">Suma del valor neto de cada factura, tal como viene en la facturación</div>
      {items.map((x) => (
        <div key={x.month} className="flex items-center justify-between py-1.5 border-b border-gray-100 text-[14px]" data-testid={`zonas-hot-billing-${x.month}`}>
          <span className="font-semibold">{x.label} <span className="text-gray-400 text-[11px]">({x.billed} de {x.units} con factura)</span></span>
          <span className="font-black text-gray-900">{x.total ? rd(x.total) : "—"}</span>
        </div>
      ))}
      <div className="flex items-center justify-between py-2 text-[14px] font-black border-t-2 border-gray-900 mt-1">
        <span>Total</span><span>{rd(total)}</span>
      </div>
      {withSales.length > 0 && (
        <p className="text-[13px] font-semibold text-gray-600 mt-2" data-testid="zonas-hot-billing-analysis">
          Promedio mensual de {rd(total / withSales.length)}. {top.label} fue el mes de mayor facturación con {rd(top.total)}
          {" "}({Math.round((top.total / total) * 100)}% del total). {billed} de {units} unidades tienen factura asociada.
        </p>
      )}
    </div>
  );
}

export default function ZonasHotInventoryTable({ d, full }) {
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  if (!d) return null;
  const rows = sort.key ? [...d.rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    if (x == null || x === "") return 1;
    if (y == null || y === "") return -1;
    const c = typeof x === "number" ? x - y : String(x).localeCompare(String(y), "es");
    return sort.dir === "asc" ? c : -c;
  }) : d.rows;
  const toggle = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  return (
    <div className="p-5" data-testid="zonas-hot-inventory">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[13px] text-gray-500 font-semibold mt-0.5">
            Días que cada chasis de Zonas Hot llevaba en inventario el día de su facturación (inventario de referencia: {fmtDate(d.cut_date)}).
          </p>
        </div>
        <div className="flex gap-4 text-[13px] font-bold text-gray-600" data-testid="zonas-hot-inventory-stats">
          <span>Con dato: <b className="text-gray-900">{d.with_data} de {d.total}</b></span>
          <span>Promedio: <b className="text-gray-900">{d.avg_days ?? "—"} días</b></span>
          <span>Más de 365: <b className="text-red-600">{d.over_365}</b></span>
        </div>
      </div>
      <div className="flex gap-8 flex-wrap mb-5 p-4 rounded-xl bg-orange-50/50 border border-orange-100">
        <div className="flex-1 min-w-[260px] space-y-6">
          <AvgList title="Promedio por sucursal" items={avgBy(d.rows, "sucursal")} testid="zonas-hot-avg-sucursal"/>
          <Billing items={d.billing_by_month || []}/>
        </div>
        <AvgList title="Promedio por modelo" items={avgBy(d.rows, "modelo")} testid="zonas-hot-avg-modelo"/>
      </div>
      <div className={`overflow-x-auto ${full ? "" : "max-h-[520px]"}`}>
        <table className="w-full border-collapse text-[14px]">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[12px] uppercase tracking-wider text-gray-500 border-b border-gray-200 text-left">
              {COLS.map(([key, h]) => (
                <th key={key} className="px-3 py-2 font-extrabold whitespace-nowrap">
                  <button type="button" onClick={() => toggle(key)} data-testid={`zonas-hot-sort-${key}`}
                          className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-orange-600 transition-colors ${sort.key === key ? "text-orange-600" : ""}`}>
                    {h}
                    {sort.key === key ? (sort.dir === "desc" ? <ArrowDown size={12}/> : <ArrowUp size={12}/>) : <ArrowUpDown size={12} className="opacity-40"/>}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.chasis}-${i}`} className="border-b border-gray-100" data-testid={`zonas-hot-inv-row-${r.chasis}`}>
                <td className="px-3 py-2 font-mono text-[13px]">{r.chasis}</td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.salon}</td>
                <td className="px-3 py-2 font-semibold">{r.sucursal}</td>
                <td className="px-3 py-2 font-semibold">{r.modelo || "—"}</td>
                <td className="px-3 py-2 font-semibold text-center">{r.anio_modelo || "—"}</td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.vendedor}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.fecha_venta)}<span className="text-[10px] text-gray-400 ml-1">({r.fecha_origen})</span></td>
                <td className={`px-3 py-2 text-right font-black ${tone(r.dias_al_vender)}`}>{r.dias_al_vender ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
